import { useState } from 'react'

import { Logo } from '@/components/brand/Logo'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ToggleTabs } from '@/components/ui/ToggleTabs'
import { cn } from '@/lib/utils'
import {
  BOOKKEEPER_TAB_ORDER,
  BOOKKEEPER_TAB_ROW,
  BookkeeperTab,
  CaiProduct,
  DEFAULT_PITCH_CTA,
  DisabledTabTreatment,
  FINANCE_GROUP,
  type NavGroup,
  orderedTabs,
  PRODUCT_COPY,
  ProductTab,
  SETTINGS_ROW,
  TAB_ENTITY,
  TAB_LABEL,
  TAB_PRODUCTS,
  TABBED_INSURANCE_GROUPS,
  VAULT_GROUP,
} from './navModel'
import { NavGroupBlock, NavRow } from './NavRow'

/** One pitch per product the tab covers but the account does not have. */
function TabPitch({ products }: { products: CaiProduct[] }) {
  return (
    <div className="flex flex-col gap-3 px-3 py-4">
      {products.map((product) => {
        const copy = PRODUCT_COPY[product]
        return (
          <div
            key={product}
            className={cn(
              'flex flex-col items-start gap-2 rounded-lg border p-3',
              copy.muted ? 'border-ink-50' : 'border-brand-200 bg-brand-50'
            )}
          >
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-neutral-900">{copy.label}</span>
              {copy.tag && (
                <Badge
                  variant={copy.muted ? 'secondary' : 'default'}
                  className="px-1.5 py-0 text-[10px] leading-4 tracking-wider"
                >
                  {copy.tag}
                </Badge>
              )}
            </div>
            <p className="text-xs leading-relaxed text-neutral-600">{copy.pitch}</p>
            <Button
              size={copy.muted ? 'xs' : 'sm'}
              variant={copy.muted ? 'link' : 'default'}
              className={cn(copy.muted && 'px-0')}
            >
              {copy.cta ?? DEFAULT_PITCH_CTA}
            </Button>
            {copy.note && <p className="text-[10px] leading-4 text-neutral-500">{copy.note}</p>}
          </div>
        )
      })}
    </div>
  )
}

function tabGroups(
  tab: ProductTab,
  has: (product: CaiProduct) => boolean,
  bookkeeperTab: BookkeeperTab
): NavGroup[] {
  if (tab === ProductTab.INSURANCE) return TABBED_INSURANCE_GROUPS
  if (tab === ProductTab.COMPLIANCE) return [VAULT_GROUP]

  const groups: NavGroup[] = []
  if (has(CaiProduct.BOOKKEEPER)) {
    groups.push({
      label: 'Bookkeeping',
      rows: BOOKKEEPER_TAB_ORDER.map((section) => ({
        ...BOOKKEEPER_TAB_ROW[section],
        active: section === bookkeeperTab,
      })),
    })
  }
  if (has(CaiProduct.CFO)) groups.push(FINANCE_GROUP)
  return groups
}

// The Insurance tab's natural height — every tab is pinned to it so the examples line up.
const NAV_HEIGHT = 'h-[646px]'

interface TabbedAccountSideNavProps {
  products: CaiProduct[]
  disabledTabs?: DisabledTabTreatment
  /** Products to leave unpitched — the account is not eligible, so there is nothing to offer. */
  hidePitchFor?: CaiProduct[]
  /** Which Bookkeeper section reads as selected. */
  activeBookkeeperTab?: BookkeeperTab
  orgName?: string
}

export function TabbedAccountSideNav({
  products,
  disabledTabs = DisabledTabTreatment.PITCH,
  hidePitchFor = [],
  activeBookkeeperTab = BookkeeperTab.OVERVIEW,
  orgName = 'Redline Freight',
}: TabbedAccountSideNavProps) {
  const has = (product: CaiProduct) => products.includes(product)
  const isTabEnabled = (tab: ProductTab) => TAB_PRODUCTS[tab].some(has)

  const tabs = orderedTabs(products)
  const visibleTabs = disabledTabs === DisabledTabTreatment.HIDDEN ? tabs.filter(isTabEnabled) : tabs
  const [active, setActive] = useState<string>(
    () => (tabs.find(isTabEnabled) ?? ProductTab.INSURANCE) as string
  )

  // A hidden-tab nav can be left pointing at a tab it no longer renders, and with no
  // products at all there is no tab to fall back to — `.at` keeps that case in the type.
  const current: ProductTab | undefined = visibleTabs.includes(active as ProductTab)
    ? (active as ProductTab)
    : visibleTabs.at(0)
  const missing = current
    ? TAB_PRODUCTS[current].filter((product) => !has(product) && !hidePitchFor.includes(product))
    : []
  // Two mt-autos would split the free space between them, floating the pitch off the bottom.
  const pitchPinned = !!current && isTabEnabled(current) && missing.length > 0

  return (
    <div className={cn(NAV_HEIGHT, 'border-ink-50 flex w-64 shrink-0 flex-col rounded-sm border bg-white')}>
      <div className="px-4 pt-4 pb-3">
        <Logo size="small" width={64} />
      </div>
      <div className="text-md px-4 pt-2 pb-3 font-medium text-neutral-900 capitalize">{orgName}</div>

      {visibleTabs.length > 0 && (
        <div className="px-2 pb-2">
          <ToggleTabs
            value={current}
            onValueChange={setActive}
            listClassName="border-ink-50 h-8 w-full"
            triggerClassName="flex-1 cursor-pointer px-2 text-xs"
            items={visibleTabs.map((tab) => ({ value: tab, label: TAB_LABEL[tab] }))}
          />
        </div>
      )}

      <div className="border-ink-50 no-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto border-t">
        {current && isTabEnabled(current) ? (
          <>
            {tabGroups(current, has, activeBookkeeperTab).map((group, index) => (
              <NavGroupBlock key={group.label ?? `group-${index}`} group={group} />
            ))}
            {/* Only Finance covers two products, so only it can be part-owned. */}
            {missing.length > 0 && (
              <div className="mt-auto">
                <TabPitch products={missing} />
              </div>
            )}
          </>
        ) : (
          <TabPitch products={missing} />
        )}
        {current && (
          <p className={cn('px-4 py-3 text-[10px] leading-4 text-neutral-400', !pitchPinned && 'mt-auto')}>
            {TAB_ENTITY[current]}
          </p>
        )}
      </div>

      <div className="border-ink-50 mt-auto border-t px-2 py-2">
        <NavRow row={SETTINGS_ROW} />
      </div>
    </div>
  )
}
